import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Button,
  Row,
  Col,
  Badge,
  Alert,
  Input,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import classnames from 'classnames';
import DataTable from '../../components/DataTable';
import Loading from '../../components/Loading';
import { getIcon } from '../../utils/iconMapper';
import { reportesService } from '../../services/reportesService';
import { empresasService } from '../../services/empresasService';
import { sucursalesService } from '../../services/sucursalesService';
import { productosService } from '../../services/productosService';
import SnowFallComponent from 'components/SnowFallComponent';

function Reportes() {
  const [activeTab, setActiveTab] = useState('ventas');
  const [empresa, setEmpresa] = useState(null);
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSucursales, setLoadingSucursales] = useState(true);
  const [error, setError] = useState('');

  // Refs para detectar el ancho de los contenedores de botones
  const buttonsContainerVentasRef = useRef(null);
  const buttonsContainerInventarioRef = useRef(null);

  // Estados para controlar si mostrar texto en los botones
  const [showTextVentas, setShowTextVentas] = useState(true);
  const [showTextInventario, setShowTextInventario] = useState(true);

  // Hook para detectar el ancho del contenedor y mostrar/ocultar texto
  useEffect(() => {
    const checkWidth = (ref, setShowText) => {
      if (!ref.current) return;
      const width = ref.current.offsetWidth;
      // Si el contenedor tiene menos de 300px, ocultar el texto
      setShowText(width >= 300);
    };

    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const width = entry.contentRect.width;
        // Determinar qué contenedor es y actualizar el estado correspondiente
        if (entry.target === buttonsContainerVentasRef.current) {
          setShowTextVentas(width >= 300);
        } else if (entry.target === buttonsContainerInventarioRef.current) {
          setShowTextInventario(width >= 300);
        }
      });
    });

    // Observar los contenedores
    if (buttonsContainerVentasRef.current) {
      resizeObserver.observe(buttonsContainerVentasRef.current);
      checkWidth(buttonsContainerVentasRef, setShowTextVentas);
    }
    if (buttonsContainerInventarioRef.current) {
      resizeObserver.observe(buttonsContainerInventarioRef.current);
      checkWidth(buttonsContainerInventarioRef, setShowTextInventario);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [activeTab]); // Re-evaluar cuando cambie la pestaña activa

  // Filtros para reporte de ventas
  const [filtroSucursalVentas, setFiltroSucursalVentas] = useState('');
  const [filtroFechaDesdeVentas, setFiltroFechaDesdeVentas] = useState('');
  const [filtroFechaHastaVentas, setFiltroFechaHastaVentas] = useState('');
  const [reporteVentas, setReporteVentas] = useState([]);

  // Filtros para reporte de inventario
  const [filtroSucursalInventario, setFiltroSucursalInventario] = useState('');
  const [filtroSoloStockBajo, setFiltroSoloStockBajo] = useState(false);
  const [reporteInventario, setReporteInventario] = useState([]);

  // Filtros para reporte de ventas por producto
  const [filtroSucursalVentasProducto, setFiltroSucursalVentasProducto] = useState('');
  const [filtroProducto, setFiltroProducto] = useState('');
  const [filtroFechaDesdeVentasProducto, setFiltroFechaDesdeVentasProducto] = useState('');
  const [filtroFechaHastaVentasProducto, setFiltroFechaHastaVentasProducto] = useState('');
  const [productos, setProductos] = useState([]);
  const [reporteVentasProducto, setReporteVentasProducto] = useState([]);

  useEffect(() => {
    loadEmpresaYSucursales();
  }, []);

  useEffect(() => {
    if (!loadingSucursales && empresa) {
      if (activeTab === 'ventas') {
        loadReporteVentas();
      } else if (activeTab === 'inventario') {
        loadReporteInventario();
      } else if (activeTab === 'ventas-producto') {
        loadProductos();
        loadReporteVentasPorProducto();
      }
    }
  }, [activeTab, filtroSucursalVentas, filtroFechaDesdeVentas, filtroFechaHastaVentas, filtroSucursalInventario, filtroSoloStockBajo, filtroSucursalVentasProducto, filtroProducto, filtroFechaDesdeVentasProducto, filtroFechaHastaVentasProducto, loadingSucursales, empresa]);

  const loadEmpresaYSucursales = async () => {
    try {
      setLoadingSucursales(true);
      const empresasResponse = await empresasService.getAll();
      if (empresasResponse.success && empresasResponse.data) {
        const empresasData = Array.isArray(empresasResponse.data) ? empresasResponse.data : [];
        const empresaActiva = empresasData.find(emp => emp.Activo !== false && emp.activo !== false) || empresasData[0];
        if (empresaActiva) {
          setEmpresa(empresaActiva);
          const sucursalesResponse = await sucursalesService.getAll(empresaActiva.EmpresaID || empresaActiva.empresaID);
          if (sucursalesResponse.success && sucursalesResponse.data) {
            const sucursalesData = Array.isArray(sucursalesResponse.data) ? sucursalesResponse.data : [];
            const normalized = sucursalesData.map(suc => ({
              SucursalID: suc.SucursalID || suc.sucursalID,
              Nombre: suc.Nombre || suc.nombre || '',
            }));
            setSucursales(normalized);
            if (normalized.length > 0) {
              setFiltroSucursalVentas(normalized[0].SucursalID.toString());
              setFiltroSucursalInventario(normalized[0].SucursalID.toString());
              setFiltroSucursalVentasProducto(normalized[0].SucursalID.toString());
            }
          }
        }
      }
    } catch (err) {
      setError('Error al cargar datos de la empresa');
    } finally {
      setLoadingSucursales(false);
    }
  };

  const loadProductos = async () => {
    if (!empresa) return;
    try {
      const response = await productosService.getAll(
        empresa.EmpresaID || empresa.empresaID,
        filtroSucursalVentasProducto ? parseInt(filtroSucursalVentasProducto) : null
      );
      if (response.success && response.data) {
        const productosData = Array.isArray(response.data) ? response.data : [];
        setProductos(productosData.map(p => ({
          ProductoID: p.ProductoID || p.productoID,
          Nombre: p.Nombre || p.nombre || '',
          SKU: p.SKU || p.sku || '',
        })));
      }
    } catch (err) {
    }
  };

  const loadReporteVentas = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await reportesService.getReporteVentas(
        filtroSucursalVentas ? parseInt(filtroSucursalVentas) : null,
        empresa?.EmpresaID || empresa?.empresaID || null,
        filtroFechaDesdeVentas || null,
        filtroFechaHastaVentas || null
      );

      if (response.success && response.data) {
        const reporteData = Array.isArray(response.data) ? response.data : [];
        const normalized = reporteData.map(item => ({
          SucursalID: item.SucursalID || item.sucursalID,
          SucursalNombre: item.SucursalNombre || item.sucursalNombre || '',
          EmpresaNombre: item.EmpresaNombre || item.empresaNombre || '',
          TotalVentas: item.TotalVentas !== undefined ? parseInt(item.TotalVentas) : (item.totalVentas !== undefined ? parseInt(item.totalVentas) : 0),
          TotalIngresos: item.TotalIngresos !== undefined ? parseFloat(item.TotalIngresos) : (item.totalIngresos !== undefined ? parseFloat(item.totalIngresos) : 0),
          TotalSubtotal: item.TotalSubtotal !== undefined ? parseFloat(item.TotalSubtotal) : (item.totalSubtotal !== undefined ? parseFloat(item.totalSubtotal) : 0),
          TotalImpuestos: item.TotalImpuestos !== undefined ? parseFloat(item.TotalImpuestos) : (item.totalImpuestos !== undefined ? parseFloat(item.totalImpuestos) : 0),
          TotalDescuentos: item.TotalDescuentos !== undefined ? parseFloat(item.TotalDescuentos) : (item.totalDescuentos !== undefined ? parseFloat(item.totalDescuentos) : 0),
        }));
        setReporteVentas(normalized);
      } else {
        setError(response.Message || response.message || 'Error al cargar el reporte de ventas');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const loadReporteInventario = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await reportesService.getReporteInventario(
        filtroSucursalInventario ? parseInt(filtroSucursalInventario) : null,
        empresa?.EmpresaID || empresa?.empresaID || null,
        filtroSoloStockBajo || null
      );

      if (response.success && response.data) {
        const reporteData = Array.isArray(response.data) ? response.data : [];
        const normalized = reporteData.map(item => ({
          InventarioID: item.InventarioID || item.inventarioID,
          ProductoID: item.ProductoID || item.productoID,
          ProductoNombre: item.ProductoNombre || item.productoNombre || '',
          ProductoSKU: item.ProductoSKU || item.productoSKU || '',
          SucursalID: item.SucursalID || item.sucursalID,
          SucursalNombre: item.SucursalNombre || item.sucursalNombre || '',
          EmpresaNombre: item.EmpresaNombre || item.empresaNombre || '',
          StockActual: item.StockActual !== undefined ? parseFloat(item.StockActual) : (item.stockActual !== undefined ? parseFloat(item.stockActual) : 0),
          StockMinimo: item.StockMinimo !== undefined ? parseFloat(item.StockMinimo) : (item.stockMinimo !== undefined ? parseFloat(item.stockMinimo) : 0),
          StockBajo: item.StockBajo !== undefined ? item.StockBajo : (item.stockBajo !== undefined ? item.stockBajo : false),
          ValorInventario: item.ValorInventario !== undefined ? parseFloat(item.ValorInventario) : (item.valorInventario !== undefined ? parseFloat(item.valorInventario) : 0),
        }));
        setReporteInventario(normalized);
      } else {
        setError(response.Message || response.message || 'Error al cargar el reporte de inventario');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const loadReporteVentasPorProducto = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await reportesService.getReporteVentasPorProducto(
        filtroProducto ? parseInt(filtroProducto) : null,
        filtroSucursalVentasProducto ? parseInt(filtroSucursalVentasProducto) : null,
        empresa?.EmpresaID || empresa?.empresaID || null,
        filtroFechaDesdeVentasProducto || null,
        filtroFechaHastaVentasProducto || null
      );

      if (response.success && response.data) {
        const reporteData = Array.isArray(response.data) ? response.data : [];
        const normalized = reporteData.map(item => ({
          ProductoID: item.ProductoID || item.productoID,
          ProductoNombre: item.ProductoNombre || item.productoNombre || '',
          ProductoSKU: item.ProductoSKU || item.productoSKU || '',
          TotalVentas: item.TotalVentas !== undefined ? parseInt(item.TotalVentas) : (item.totalVentas !== undefined ? parseInt(item.totalVentas) : 0),
          TotalCantidadVendida: item.TotalCantidadVendida !== undefined ? parseFloat(item.TotalCantidadVendida) : (item.totalCantidadVendida !== undefined ? parseFloat(item.totalCantidadVendida) : 0),
          TotalIngresos: item.TotalIngresos !== undefined ? parseFloat(item.TotalIngresos) : (item.totalIngresos !== undefined ? parseFloat(item.totalIngresos) : 0),
          PromedioPrecio: item.PromedioPrecio !== undefined ? parseFloat(item.PromedioPrecio) : (item.promedioPrecio !== undefined ? parseFloat(item.promedioPrecio) : 0),
        }));
        setReporteVentasProducto(normalized);
      } else {
        setError(response.Message || response.message || 'Error al cargar el reporte de ventas por producto');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const exportarCSV = (datos, nombreArchivo, columnas) => {
    if (!datos || datos.length === 0) {
      setError('No hay datos para exportar');
      return;
    }

    // Crear encabezados
    const headers = columnas.map(col => col.label).join(',');

    // Crear filas
    const rows = datos.map(item => {
      return columnas.map(col => {
        const value = col.render ? col.render(item[col.accessor], item) : (item[col.accessor] || '');
        // Escapar comillas y envolver en comillas si contiene comas
        const stringValue = String(value).replace(/"/g, '""');
        return stringValue.includes(',') ? `"${stringValue}"` : stringValue;
      }).join(',');
    });

    // Combinar todo
    const csvContent = [headers, ...rows].join('\n');

    // Crear blob y descargar
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${nombreArchivo}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('es-MX', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value || 0);
  };

  const ventasColumns = [
    {
      key: 'sucursal',
      label: 'Sucursal',
      accessor: 'SucursalNombre',
    },
    {
      key: 'empresa',
      label: 'Empresa',
      accessor: 'EmpresaNombre',
    },
    {
      key: 'totalVentas',
      label: 'Total Ventas',
      accessor: 'TotalVentas',
      render: (value) => value.toLocaleString('es-MX'),
    },
    {
      key: 'totalSubtotal',
      label: 'Subtotal',
      accessor: 'TotalSubtotal',
      render: (value) => formatCurrency(value),
    },
    {
      key: 'totalImpuestos',
      label: 'Impuestos',
      accessor: 'TotalImpuestos',
      render: (value) => formatCurrency(value),
    },
    {
      key: 'totalDescuentos',
      label: 'Descuentos',
      accessor: 'TotalDescuentos',
      render: (value) => formatCurrency(value),
    },
    {
      key: 'totalIngresos',
      label: 'Total Ingresos',
      accessor: 'TotalIngresos',
      render: (value) => formatCurrency(value),
    },
  ];

  const inventarioColumns = [
    {
      key: 'sucursal',
      label: 'Sucursal',
      accessor: 'SucursalNombre',
    },
    {
      key: 'producto',
      label: 'Producto',
      accessor: 'ProductoNombre',
    },
    {
      key: 'sku',
      label: 'SKU',
      accessor: 'ProductoSKU',
    },
    {
      key: 'stockActual',
      label: 'Stock Actual',
      accessor: 'StockActual',
      render: (value) => formatNumber(value),
    },
    {
      key: 'stockMinimo',
      label: 'Stock Mínimo',
      accessor: 'StockMinimo',
      render: (value) => formatNumber(value),
    },
    {
      key: 'stockBajo',
      label: 'Estado',
      accessor: 'StockBajo',
      render: (value) => (
        <Badge color={value ? 'danger' : 'success'}>
          {value ? 'Stock Bajo' : 'Normal'}
        </Badge>
      ),
    },
    {
      key: 'valorInventario',
      label: 'Valor Inventario',
      accessor: 'ValorInventario',
      render: (value) => formatCurrency(value),
    },
  ];

  const ventasProductoColumns = [
    {
      key: 'producto',
      label: 'Producto',
      accessor: 'ProductoNombre',
    },
    {
      key: 'sku',
      label: 'SKU',
      accessor: 'ProductoSKU',
    },
    {
      key: 'totalVentas',
      label: 'Total Ventas',
      accessor: 'TotalVentas',
      render: (value) => value.toLocaleString('es-MX'),
    },
    {
      key: 'totalCantidadVendida',
      label: 'Cantidad Vendida',
      accessor: 'TotalCantidadVendida',
      render: (value) => formatNumber(value),
    },
    {
      key: 'promedioPrecio',
      label: 'Precio Promedio',
      accessor: 'PromedioPrecio',
      render: (value) => formatCurrency(value),
    },
    {
      key: 'totalIngresos',
      label: 'Total Ingresos',
      accessor: 'TotalIngresos',
      render: (value) => formatCurrency(value),
    },
  ];

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  return (
    <div className="content">
      <SnowFallComponent />
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">Reportes</CardTitle>
            </CardHeader>
            <CardBody>
              {error && (
                <Alert color="danger" className="mb-3">
                  {error}
                </Alert>
              )}

              <Nav tabs>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === 'ventas' })}
                    onClick={() => toggleTab('ventas')}
                    style={{ cursor: 'pointer' }}
                  >
                    Reporte de Ventas
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === 'inventario' })}
                    onClick={() => toggleTab('inventario')}
                    style={{ cursor: 'pointer' }}
                  >
                    Reporte de Inventario
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === 'ventas-producto' })}
                    onClick={() => toggleTab('ventas-producto')}
                    style={{ cursor: 'pointer' }}
                  >
                    Ventas por Producto
                  </NavLink>
                </NavItem>
              </Nav>

              <TabContent activeTab={activeTab}>
                <TabPane tabId="ventas">
                  <Row className="mt-3">
                    <Col md="3">
                      <label>Sucursal</label>
                      <select
                        className="form-control"
                        value={filtroSucursalVentas}
                        onChange={(e) => setFiltroSucursalVentas(e.target.value)}
                        disabled={loadingSucursales}
                      >
                        <option value="">Todas</option>
                        {sucursales.map((suc) => (
                          <option key={suc.SucursalID} value={suc.SucursalID}>
                            {suc.Nombre}
                          </option>
                        ))}
                      </select>
                    </Col>
                    <Col md="3">
                      <label>Fecha Desde</label>
                      <Input
                        type="date"
                        value={filtroFechaDesdeVentas}
                        onChange={(e) => setFiltroFechaDesdeVentas(e.target.value)}
                      />
                    </Col>
                    <Col md="3">
                      <label>Fecha Hasta</label>
                      <Input
                        type="date"
                        value={filtroFechaHastaVentas}
                        onChange={(e) => setFiltroFechaHastaVentas(e.target.value)}
                      />
                    </Col>
                    <Col md="3" className="d-flex align-items-end" ref={buttonsContainerVentasRef}>
                      <Button color="primary" onClick={loadReporteVentas} disabled={loading || !empresa}>
                        <i className={getIcon("tim-icons icon-zoom-split")} /> {showTextVentas && <span>Generar</span>}
                      </Button>
                      <Button
                        color="success"
                        className="ml-2"
                        onClick={() => exportarCSV(reporteVentas, 'reporte_ventas', ventasColumns)}
                        disabled={reporteVentas.length === 0}
                      >
                        <i className="fas fa-file-excel" /> {showTextVentas && <span>Exportar CSV</span>}
                      </Button>
                    </Col>
                  </Row>
                  <Row className="mt-3">
                    <Col md="12">
                      {loading ? (
                        <Loading />
                      ) : (
                        <DataTable columns={ventasColumns} data={reporteVentas} />
                      )}
                    </Col>
                  </Row>
                </TabPane>

                <TabPane tabId="inventario">
                  <Row className="mt-3">
                    <Col md="3">
                      <label>Sucursal</label>
                      <select
                        className="form-control"
                        value={filtroSucursalInventario}
                        onChange={(e) => setFiltroSucursalInventario(e.target.value)}
                        disabled={loadingSucursales}
                      >
                        <option value="">Todas</option>
                        {sucursales.map((suc) => (
                          <option key={suc.SucursalID} value={suc.SucursalID}>
                            {suc.Nombre}
                          </option>
                        ))}
                      </select>
                    </Col>
                    <Col md="3" className="d-flex align-items-end">
                      <label className="mr-2">
                        <input
                          type="checkbox"
                          checked={filtroSoloStockBajo}
                          onChange={(e) => setFiltroSoloStockBajo(e.target.checked)}
                          className="mr-2"
                        />
                        Solo stock bajo
                      </label>
                    </Col>
                    <Col md="6" className="d-flex align-items-end" ref={buttonsContainerInventarioRef}>
                      <Button color="primary" onClick={loadReporteInventario} disabled={loading || !empresa}>
                        <i className={getIcon("tim-icons icon-zoom-split")} /> {showTextInventario && <span>Generar</span>}
                      </Button>
                      <Button
                        color="success"
                        className="ml-2"
                        onClick={() => exportarCSV(reporteInventario, 'reporte_inventario', inventarioColumns)}
                        disabled={reporteInventario.length === 0}
                      >
                        <i className="fas fa-file-excel" /> {showTextInventario && <span>Exportar CSV</span>}
                      </Button>
                    </Col>
                  </Row>
                  <Row className="mt-3">
                    <Col md="12">
                      {loading ? (
                        <Loading />
                      ) : (
                        <DataTable columns={inventarioColumns} data={reporteInventario} />
                      )}
                    </Col>
                  </Row>
                </TabPane>

                <TabPane tabId="ventas-producto">
                  <Row className="mt-3">
                    <Col md="3">
                      <label>Sucursal</label>
                      <select
                        className="form-control"
                        value={filtroSucursalVentasProducto}
                        onChange={(e) => {
                          setFiltroSucursalVentasProducto(e.target.value);
                          loadProductos();
                        }}
                        disabled={loadingSucursales}
                      >
                        <option value="">Todas</option>
                        {sucursales.map((suc) => (
                          <option key={suc.SucursalID} value={suc.SucursalID}>
                            {suc.Nombre}
                          </option>
                        ))}
                      </select>
                    </Col>
                    <Col md="3">
                      <label>Producto</label>
                      <Input
                        type="select"
                        value={filtroProducto}
                        onChange={(e) => setFiltroProducto(e.target.value)}
                      >
                        <option value="">Todos</option>
                        {productos.map((prod) => (
                          <option key={prod.ProductoID} value={prod.ProductoID}>
                            {prod.SKU} - {prod.Nombre}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col md="2">
                      <label>Fecha Desde</label>
                      <Input
                        type="date"
                        value={filtroFechaDesdeVentasProducto}
                        onChange={(e) => setFiltroFechaDesdeVentasProducto(e.target.value)}
                      />
                    </Col>
                    <Col md="2">
                      <label>Fecha Hasta</label>
                      <Input
                        type="date"
                        value={filtroFechaHastaVentasProducto}
                        onChange={(e) => setFiltroFechaHastaVentasProducto(e.target.value)}
                      />
                    </Col>
                    <Col md="2" className="d-flex align-items-end">
                      <Button color="primary" onClick={loadReporteVentasPorProducto} disabled={loading || !empresa}>
                        <i className={getIcon("tim-icons icon-zoom-split")} />
                      </Button>
                      <Button
                        color="success"
                        className="ml-2"
                        onClick={() => exportarCSV(reporteVentasProducto, 'reporte_ventas_producto', ventasProductoColumns)}
                        disabled={reporteVentasProducto.length === 0}
                      >
                        <i className="fas fa-file-excel" />
                      </Button>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="12">
                      {loading ? (
                        <Loading />
                      ) : (
                        <DataTable columns={ventasProductoColumns} data={reporteVentasProducto} />
                      )}
                    </Col>
                  </Row>
                </TabPane>
              </TabContent>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default Reportes;

